import { Badge, Group, Table } from '@mantine/core'
import { useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { DataTable } from '@/components/ui-shared/Tables/DataTable'
import { SortableLabel } from '@/components/ui-shared/Tables/SortableLabel/SortableLabel'
import { useHasPermissions } from '@/permissions/useHasPermissions'
import { CrudAction, Resource } from '@/types/permissions'
import { User } from '@/types/user'
import { Workspace, WorkspaceMember } from '@/types/workspace'
import { getFormattedDate } from '@/utils/date'
import { SortConfig } from '@/utils/sorting'
import { DeactivateMemberModal } from '../WorkspaceMembers/DeactivateMemberModal'
import { MemberRoleBadge } from '../WorkspaceMembers/MemberRoleBadge'
import { MemberStatusBadge } from '../WorkspaceMembers/MemberStatusBadge'
import { ResendInviteModal } from '../WorkspaceMembers/ResendInviteModal'
import { UpdateMemberModal } from '../WorkspaceMembers/UpdateMember/UpdateMemberModal'
import { MembersListActions } from './MembersListActions'

// TODO: Add backend sorting support
export type TableSortConfig = SortConfig<'full_name'>

type MembersListProps = {
  workspace: Workspace
  members: WorkspaceMember[]
  sortConfig: TableSortConfig
  myUser?: User
  isLoading?: boolean
  hasError?: boolean
  onSort: (sortConfig: TableSortConfig) => void
  onReload: () => void
}

export const MembersList = ({
  workspace,
  members,
  sortConfig,
  myUser,
  isLoading,
  hasError,
  onSort,
  onReload
}: MembersListProps) => {
  const [memberToResendInvite, setMemberToResendInvite] =
    useState<WorkspaceMember | null>(null)

  const [memberToUpdate, setMemberToUpdate] = useState<WorkspaceMember | null>(
    null
  )

  const [memberToDeactivate, setMemberToDeactivate] =
    useState<WorkspaceMember | null>(null)

  const [canInviteMember, canUpdateMember, canDeleteMember] = useHasPermissions(
    Resource.WorkspaceUsers,
    [CrudAction.Create, CrudAction.Update, CrudAction.Delete]
  )

  return (
    <>
      <DataTable
        hasItems={members.length > 0}
        isLoading={isLoading}
        hasError={hasError}
        emptyMessage={<FormattedMessage id="workspace.members.list.empty" />}
        onReload={onReload}
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>
              <SortableLabel
                label={
                  <FormattedMessage id="workspace.members.list.columns.name" />
                }
                sortKey="full_name"
                sortConfig={sortConfig}
                disabled
                // TODO: Uncomment this when backend sorting is implemented
                // disabled={members.length === 0}
                onSort={onSort}
              />
            </Table.Th>

            <Table.Th>
              <FormattedMessage id="workspace.members.list.columns.email" />
            </Table.Th>

            <Table.Th>
              <FormattedMessage id="workspace.members.list.columns.joined" />
            </Table.Th>

            <Table.Th>
              <FormattedMessage id="workspace.members.list.columns.role" />
            </Table.Th>

            <Table.Th>
              <FormattedMessage id="workspace.members.list.columns.status" />
            </Table.Th>

            <Table.Th>&nbsp;</Table.Th>
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {members.map((member) => (
            <Table.Tr key={member.id}>
              <Table.Td>
                <Group align="center" gap="xs">
                  <span>{member.user.full_name || '-'}</span>

                  {myUser?.id === member.user.id && (
                    <Badge color="brand-secondary" size="xs" radius="sm">
                      <FormattedMessage id="workspace.members.you" />
                    </Badge>
                  )}
                </Group>
              </Table.Td>

              <Table.Td>{member.user.email}</Table.Td>

              <Table.Td>
                {getFormattedDate(member.created_at, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </Table.Td>

              <Table.Td>
                <MemberRoleBadge role={member.role} />
              </Table.Td>

              <Table.Td>
                <MemberStatusBadge status={member.status} />
              </Table.Td>

              <Table.Td>
                <MembersListActions
                  isCurrentUser={myUser?.id === member.user.id}
                  memberRole={member.role}
                  memberStatus={member.status}
                  canResendInvite={canInviteMember}
                  canUpdateMember={canUpdateMember}
                  canDeleteMember={canDeleteMember}
                  onResendInvite={() => setMemberToResendInvite(member)}
                  onUpdate={() => setMemberToUpdate(member)}
                  onDeactivate={() => setMemberToDeactivate(member)}
                />
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </DataTable>

      <ResendInviteModal
        member={memberToResendInvite || undefined}
        workspace={workspace}
        opened={!!memberToResendInvite}
        onClose={() => setMemberToResendInvite(null)}
      />

      <UpdateMemberModal
        workspaceId={workspace.id}
        opened={!!memberToUpdate}
        member={memberToUpdate || undefined}
        onClose={() => setMemberToUpdate(null)}
      />

      <DeactivateMemberModal
        member={memberToDeactivate || undefined}
        workspace={workspace}
        opened={!!memberToDeactivate}
        onClose={() => setMemberToDeactivate(null)}
      />
    </>
  )
}
